import classNames from 'classnames';
import { Apple, Facebook, Instagram, Linkedin, Smartphone } from 'lucide-react';
import { string } from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { NamedLink } from '../../../../components';
import nowLogo from '../../../../assets/images/now-logo.png';
import vision30 from '../../../../assets/vision_30.png';
import { injectIntl } from '../../../../util/reactIntl';
import css from './CustomerFooter.module.css';

const CUSTOMER_APP_APP_STORE = 'https://apps.apple.com/in/app/now-offers-customer/id6742392519';
const CUSTOMER_APP_PLAY_STORE =
  'https://play.google.com/store/apps/details?id=com.now.customer&pcampaignid=web_share';
const MERCHANT_APP_APP_STORE = 'https://apps.apple.com/in/app/now-offers-merchant/id6742392597';
const MERCHANT_APP_PLAY_STORE =
  'https://play.google.com/store/apps/details?id=com.now.merchant&pcampaignid=web_share';

const MERCHANT_LANDING_PATH = '/merchant';

const lucideProps = {
  fill: 'none',
  'aria-hidden': true,
};

const CustomerFooter = props => {
  const { intl } = props;
  const arabicLanguage =
    typeof window !== 'undefined' && localStorage.getItem('language') === 'Arabic';
  const currentYear = new Date().getFullYear();

  const currentUser = useSelector(state => state?.user?.currentUser);
  const location = useLocation();
  const userType = currentUser?.attributes?.profile?.publicData?.userType;

  // Authenticated users see the app matching their account type; guests see the
  // merchant app on the merchant landing page and the customer app elsewhere.
  const isMerchant = currentUser
    ? userType === 'provider'
    : location?.pathname === MERCHANT_LANDING_PATH;

  const appStoreLink = isMerchant ? MERCHANT_APP_APP_STORE : CUSTOMER_APP_APP_STORE;
  const playStoreLink = isMerchant ? MERCHANT_APP_PLAY_STORE : CUSTOMER_APP_PLAY_STORE;

  return (
    <footer
      className={classNames(css.customerFooterWrapper, arabicLanguage && css.arabicDirection)}
    >
      <div className={css.footerGrid}>
        <div className={css.brandColumn}>
          <NamedLink name="LandingPage" className={css.logoBox}>
            <img src={nowLogo} alt="NOW Offers" className={css.logoImage} />
          </NamedLink>
          <p className={css.tagline}>
            <FormattedMessage id="CustomerFooter.tagline" />
          </p>
          <div className={css.socialIcons}>
            <a
              href="https://www.instagram.com/nowoffersapp/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className={css.socialLink}
            >
              <Instagram className={css.socialIcon} size={16} strokeWidth={2} {...lucideProps} />
            </a>
            <a
              href="https://www.facebook.com/NowOffersSA/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className={css.socialLink}
            >
              <Facebook className={css.socialIcon} size={16} strokeWidth={2} {...lucideProps} />
            </a>
            <a
              href="https://www.linkedin.com/company/nowoffers/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className={css.socialLink}
            >
              <Linkedin className={css.socialIcon} size={16} strokeWidth={2} {...lucideProps} />
            </a>
          </div>
        </div>

        <div className={css.linksColumn}>
          <div className={css.columnHeading}>
            <FormattedMessage id="CustomerFooter.company" />
          </div>
          <ul className={css.linkList}>
            <li>
              <NamedLink className={css.footerLink} name="ContactUsPage">
                <FormattedMessage id="CustomerFooter.contact" />
              </NamedLink>
            </li>
            <li>
              <NamedLink
                className={css.footerLink}
                name={arabicLanguage ? 'PrivacyPolicyArabicPage' : 'PrivacyPolicyPage'}
              >
                {intl.formatMessage({ id: 'SectionFooter.privacyPolicy' })}
              </NamedLink>
            </li>
            <li>
              <a
                className={css.footerLink}
                href={`${
                  arabicLanguage ? '/privacy-policy-arabic' : '/privacy-policy'
                }#terms-of-service`}
              >
                {intl.formatMessage({ id: 'SectionFooter.termsOfService' })}
              </a>
            </li>
            <li>
              <a
                className={css.footerLink}
                href={`${
                  arabicLanguage ? '/privacy-policy-arabic' : '/privacy-policy'
                }#merchant-sla`}
              >
                {intl.formatMessage({ id: 'SectionFooter.merchantSLA' })}
              </a>
            </li>
          </ul>
        </div>

        <div className={css.appColumn}>
          <div className={css.columnHeading}>
            <FormattedMessage id="CustomerFooter.getTheApp" />
          </div>
          <div className={css.appButtons}>
            <a
              href={appStoreLink}
              target="_blank"
              rel="noopener noreferrer"
              className={css.appButton}
            >
              <Apple className={css.appIcon} size={24} strokeWidth={2} {...lucideProps} />
              <div className={css.appButtonText}>
                <div className={css.appButtonLabel}>
                  <FormattedMessage id="CustomerFooter.downloadOnThe" />
                </div>
                <div className={css.appButtonTitle}>
                  <FormattedMessage id="CustomerFooter.appStore" />
                </div>
              </div>
            </a>
            <a
              href={playStoreLink}
              target="_blank"
              rel="noopener noreferrer"
              className={css.appButton}
            >
              <Smartphone className={css.appIcon} size={24} strokeWidth={2} {...lucideProps} />
              <div className={css.appButtonText}>
                <div className={css.appButtonLabel}>
                  <FormattedMessage id="CustomerFooter.getItOn" />
                </div>
                <div className={css.appButtonTitle}>
                  <FormattedMessage id="CustomerFooter.googlePlay" />
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>

      <div className={css.footerBottomBar}>
        <div className={css.footerBottomInner}>
          <div className={css.madeWithPill}>
            <span>
              <FormattedMessage id="CustomerFooter.madeWith" />
            </span>
            <span className={css.heart}>♥</span>
            <span>
              <FormattedMessage id="CustomerFooter.inSaudiArabia" />
            </span>
            <span className={css.pillDivider}>|</span>
            <img src={vision30} alt="Vision 2030" className={css.vision2030} />
          </div>
          <div className={css.copyright}>{intl.formatMessage({ id: 'CustomerFooter.copyright' }, { year: currentYear })}</div>
        </div>
      </div>
    </footer>
  );
};

CustomerFooter.defaultProps = {
  className: null,
  rootClassName: null,
};

CustomerFooter.propTypes = {
  className: string,
  rootClassName: string,
};

export default injectIntl(CustomerFooter);
